import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiBaseUrl } from "../../../services/api";
import SlideCanvas, { measureContent } from "./SlideCanvas";
import { setAssetFetcher, waitForAssets } from "./assets";
import { PRINT_CSS } from "./ProposalPrint";
import { CONTENT, H, W, contentLimit } from "./theme";
import ReportDocument, { printCss } from "../../Reports/ReportDocument";

// Page ouverte par Chrome headless côté serveur pour montrer les slides aux agents
// (outil offers__slide_render) et pour imprimer le PDF envoyé au client (mode « print »). Pas de session : un jeton signé de quelques minutes,
// limité à une proposition, donne accès aux données et aux images nécessaires.
const GAP = 16;
const SCALE = 0.5;

// Place par slide, pour les agents : débordement de la zone utile, place libre, titre coupé.
const LOTS_OF_ROOM = 200;
const measureSlides = (data) => {
  const slides = {};
  data.slides.forEach((item) => {
    const root = window.document.querySelector(`[data-slide-id="${item.id}"]`);
    const content = root?.querySelector("[data-offer-content]");
    // Un titre trop long pour une ligne, même réduit à 24 px, passe sur deux lignes : on compte les lignes rendues.
    const title = root?.querySelector("[data-offer-title] > div > div");
    const lineHeight = title ? parseFloat(window.getComputedStyle(title).lineHeight) : 0;
    const entry = { page: data.pages[item.id], layout: item.layout,
      title_truncated: Boolean(lineHeight && title.offsetHeight > lineHeight * 1.5) };
    const problems = [];
    if (content) {
      Object.assign(entry, measureContent(content, contentLimit(item)));
      if (entry.overflow_px > 0) {
        problems.push(`déborde de ${entry.overflow_px} px (sort de la slide)`);
      } else if (CONTENT.bottom - CONTENT.top - entry.used_px > LOTS_OF_ROOM) {
        // Peu remplie : comparée à la zone utile, pas à la limite tolérée en dessous.
        problems.push(`${CONTENT.bottom - CONTENT.top - entry.used_px} px libres : slide peu remplie`);
      }
    }
    if (entry.title_truncated) problems.push("titre trop long : il passe sur deux lignes");
    entry.problems = problems;
    slides[item.id] = entry;
  });
  return { slides };
};

const renderUrl = (token, path = "") => `${apiBaseUrl}/offers/render/${encodeURIComponent(token)}${path}`;

// Chrome headless capture la page en temps virtuel : une attente (décodage d'image, police) peut ne jamais aboutir
// avant la fin du budget. Chacune est donc bornée par un minuteur, qui avance avec ce temps virtuel.
const settle = (promise, ms = 3000) => Promise.race([
  Promise.resolve(promise).catch(() => null), new Promise((resolve) => setTimeout(resolve, ms)),
]);
const afterLayout = (callback) => {
  let done = false;
  const run = () => { if (!done) { done = true; callback(); } };
  requestAnimationFrame(run);
  setTimeout(run, 100);
};

const ProposalRender = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setAssetFetcher(async (assetId) => {
      const response = await fetch(renderUrl(token, `/assets/${assetId}`));
      if (!response.ok) throw new Error("Image indisponible");
      return response.blob();
    });
    fetch(renderUrl(token))
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error("Rendu indisponible"))))
      .then(setData)
      .catch((err) => setError(err.message));
  }, [token]);

  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!data) return undefined;
    let cancelled = false;
    (async () => {
      await settle(waitForAssets(), 8000);
      // Les images récupérées s'affichent au rendu suivant ; on attend qu'elles soient décodées
      // pour mesurer la place qu'elles occupent réellement.
      await new Promise((resolve) => setTimeout(resolve, 50));
      await settle(Promise.all([...window.document.images].map((image) => image.decode?.().catch(() => null))));
      // Les titres et intertitres sont en Montserrat : mesurer et capturer une fois la police chargée.
      await settle(window.document.fonts?.ready);
      if (cancelled) return;
      window.document.body.dataset.offerRenderReady = "1";
      if (data.mode === "measure") afterLayout(() => setReport(measureSlides(data)));
    })();
    return () => { cancelled = true; };
  }, [data]);

  if (error) return <p style={{ fontFamily: "Arial", padding: 24 }}>{error}</p>;
  if (!data) return null;

  if (data.document === "rapport") {
    // Rapport : pages A4 imprimées par Chrome ; au premier passage, repères invisibles sur les titres (pages du
    // sommaire), au second le sommaire reçoit les pages (services/report.py).
    return (
      <div>
        <style>{printCss({ title: data.title, kicker: data.meta.kicker, clientName: data.client_name,
          confidential: data.meta.confidential })}</style>
        <ReportDocument content={{ meta: data.meta, sections: data.sections }} title={data.title}
          clientName={data.client_name} embeds={data.embeds} print markers={data.markers} pages={data.toc_pages} />
      </div>
    );
  }

  const slide = (item) => (
    <SlideCanvas slide={item} meta={data.meta} estimate={data.estimate} page={data.pages[item.id]} embeds={data.embeds}
      deckTitle={data.title} />
  );

  if (data.mode === "measure") {
    // Toutes les slides en taille réelle ; le rapport est lu par le serveur (chromium --dump-dom).
    return (
      <div>
        {data.slides.map((item) => (
          <div key={item.id} data-slide-id={item.id} style={{ width: W, height: H, overflow: "hidden", position: "relative" }}>
            {slide(item)}
          </div>
        ))}
        {report && (
          <script id="offer-layout-report" type="application/json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(report).replace(/</g, "\\u003c") }} />
        )}
      </div>
    );
  }

  if (data.mode === "print") {
    // Le PDF imprimé par le serveur (chromium --print-to-pdf) : une page par slide visible, comme ProposalPrint.
    return (
      <div>
        <style>{PRINT_CSS}</style>
        {data.slides.map((item) => (
          <div key={item.id} className="offer-print-page">{slide(item)}</div>
        ))}
      </div>
    );
  }

  if (data.mode === "slide") {
    return (
      <div style={{ width: W, height: H, overflow: "hidden", margin: 0 }}>
        <style>{"html, body { margin: 0; padding: 0; background: #ffffff; }"}</style>
        {slide(data.slides[0])}
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${data.columns}, ${W * SCALE}px)`, gap: GAP, padding: GAP }}>
      <style>{"html, body { margin: 0; padding: 0; background: #cbd5e1; }"}</style>
      {data.slides.map((item) => (
        <div key={item.id} style={{ width: W * SCALE, height: H * SCALE, overflow: "hidden", position: "relative" }}>
          <div style={{ width: W, height: H, transform: `scale(${SCALE})`, transformOrigin: "top left" }}>{slide(item)}</div>
        </div>
      ))}
    </div>
  );
};

export default ProposalRender;
